<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RoundAltArrowDownIcon } from '@solar-icons/vue/linear'
import { useDisclosure } from '@/composables/useDisclosure'
import { useSidebar } from './useSidebar'

const props = defineProps<{
  id: string
  /** Header text. An unlabelled group shows its rows with no header and never closes. */
  label?: string
  /** Whether the current page's row is in this group; a folded group then marks its header. */
  holdsActive?: boolean
}>()

const { state, isGroupOpen, setGroupOpen } = useSidebar()
const rail = computed(() => state.value === 'rail')

const { isOpen, toggle, triggerAttrs, contentAttrs } = useDisclosure({
  open: computed(() => isGroupOpen(props.id)),
  onOpenChange: (value) => setGroupOpen(props.id, value),
})

const showsRows = computed(() => !props.label || isOpen.value)
const marksActive = computed(() => props.holdsActive && !showsRows.value)

/** Set by the first fold or unfold, never at load, so rows that are already visible do not arrive. */
const motion = ref<'in' | 'out' | undefined>(undefined)
watch(showsRows, (shown) => {
  motion.value = shown ? 'in' : 'out'
})
</script>

<template>
  <div v-if="!label" role="group" class="flex flex-col gap-2">
    <slot />
  </div>

  <div v-else-if="rail" role="group" :aria-label="label" class="relative flex flex-col gap-2">
    <span class="absolute -top-4 inset-x-4 h-px bg-border" aria-hidden="true" />
    <slot />
  </div>

  <div v-else class="relative flex flex-col">
    <span class="absolute -top-4 inset-x-4 h-px bg-border" aria-hidden="true" />
    <h2 data-arrival="nav-row">
      <button
        type="button"
        v-bind="triggerAttrs"
        :class="[
          'group flex h-16 w-full select-none items-center gap-4 rounded-4 px-4 text-left outline-none hover:bg-bg-wash focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-blue',
          marksActive
            ? 'text-body-sm-medium text-text-primary'
            : 'text-body-sm text-text-secondary',
        ]"
        @click="toggle"
      >
        <span class="min-w-0 flex-1 truncate lowercase">{{ label }}</span>
        <RoundAltArrowDownIcon
          class="shrink-0 text-icon-secondary transition-transform motion-reduce:transition-none"
          :class="isOpen ? 'rotate-180 duration-slower ease-spring' : 'duration-slow ease-standard'"
          aria-hidden="true"
        />
      </button>
    </h2>
    <div class="collapsible" :data-state="showsRows ? 'open' : 'closed'">
      <div v-bind="contentAttrs" role="group" :inert="!showsRows">
        <div class="nav-rows flex flex-col gap-2 pt-2" :data-motion="motion">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>
